import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { Mail, Send, ArrowUpRight, MessageSquare, Sparkles } from 'lucide-react'
import MarketingNav from './MarketingNav'
import SiteFooter from './SiteFooter'
import './marketing-dark.css'
import './contact.css'
import { byKey } from '../lib/serviceCatalog'
import { getSession } from '../lib/session'
import { cartCount } from '../lib/cart'

/**
 * Contact — a purpose-built contact experience for ai.reright.io. The unique bit is
 * the LIVE INTENT PANEL: as you type your message, the page maps it to the matching
 * ReRight services in real time (chips pop in) — the platform demonstrating itself.
 * All copy is HEADLESS (GET /api/v1/site/contact → the admin/agent `contact` override).
 * Motion (aurora drift, chip pop, focus glow, "signal received" bloom) respects
 * prefers-reduced-motion. Distinct from the homepage's constellation.
 */
const DEFAULTS = {
  eyebrow: 'Get in touch',
  h1: 'Tell us what you want your data to do.', h1Accent: 'data',
  subhead: "A support chatbot, a voice receptionist, a private RAG API, a marketing brain — describe the outcome and we'll map it to the right ReRight services. A real person reads every message and replies fast.",
  email: 'contact@reright.io', emailNote: 'Email us directly',
  chatNote: 'Chat with AIRA — bottom-right, 24/7',
  formTitle: 'Send us a message', formSubhead: 'We reply from contact@reright.io — usually within a day.',
  successTitle: 'Signal received.', privacyNote: 'No spam, ever. Your message goes straight to the team.',
}

// Lightweight, on-device intent detection — maps the visitor's words to services.
const MATCHERS = [
  { rx: /chat ?bot|chat bubble|faq|answ|support|help ?desk/i, label: 'AI Support Chatbot' },
  { rx: /voice|phone|call|reception|answering/i, label: 'Voice AI Receptionist' },
  { rx: /\brag\b|\bapi\b|q ?& ?a|document|\bdocs\b|private q/i, label: 'Custom RAG API' },
  { rx: /whats ?app/i, label: 'WhatsApp Commerce' },
  { rx: /\bseo\b|rank|keyword|backlink/i, label: 'SEO Intelligence' },
  { rx: /market|\bads?\b|roas|analytic|revenue|attribution/i, label: 'Marketing Intelligence Brain' },
  { rx: /book|demo|meeting|appointment|schedule/i, label: 'Book a demo' },
  { rx: /reseller|white ?label|agency|own brand/i, label: 'White-Label Reseller' },
  { rx: /\bsync\b|crawl|index|always.?fresh/i, label: 'Knowledge Sync' },
  { rx: /workflow|automat|trigger|pipeline/i, label: 'Agentic Workflows' },
  { rx: /\binbox\b|shared team|assign/i, label: 'Team Inbox' },
  { rx: /intake|route|specialt|department/i, label: 'Multi-Specialty Intake' },
  { rx: /growth|funnel|conversion|experiment/i, label: 'Growth Lab' },
  { rx: /\bmcp\b|cursor|\bcli\b|claude|tool.?using/i, label: 'Tool-using Agent / MCP' },
  { rx: /integrat|webhook|adapter|connect/i, label: 'Integration Adapters' },
]
function detect(text) {
  const t = String(text || ''); const out = []
  for (const m of MATCHERS) { if (m.rx.test(t) && !out.includes(m.label)) out.push(m.label) }
  return out.slice(0, 5)
}

function Field({ label, type = 'text', value, onChange, placeholder, required, delay, autoComplete }) {
  return (
    <div className="c-rise" style={{ animationDelay: `${delay}ms` }}>
      <label className="c-label">{label}{required && <span className="text-primary-600"> *</span>}</label>
      <div className="c-field"><input className="c-input" type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} autoComplete={autoComplete} /></div>
    </div>
  )
}

export default function Contact() {
  const navigate = useNavigate()
  const authed = !!getSession()?.apiKey
  const [services, setServices] = useState([])
  const [seatCount, setSeatCount] = useState(cartCount())
  const [c, setC] = useState(DEFAULTS)
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' })
  const [status, setStatus] = useState('idle') // idle | sending | sent | error
  const [msg, setMsg] = useState('')

  useEffect(() => {
    let alive = true
    // Headless content — admin/agent editable.
    axios.get('/api/v1/site/contact')
      .then(({ data }) => { if (alive && data && data.content) setC({ ...DEFAULTS, ...data.content }) })
      .catch(() => {})
    // Catalog for the nav mega-panel.
    axios.get('/api/v1/site/catalog')
      .then(({ data }) => { if (alive && data && Array.isArray(data.services)) setServices(data.services.map((s) => ({ ...s, icon: byKey(s.key)?.icon }))) })
      .catch(() => {})
    return () => { alive = false }
  }, [])
  useEffect(() => { document.title = `${c.eyebrow ? 'Contact' : 'Contact'} ReRight — talk to the team` }, [c])
  useEffect(() => {
    const sync = () => setSeatCount(cartCount())
    sync(); window.addEventListener('reright-cart', sync)
    return () => window.removeEventListener('reright-cart', sync)
  }, [])

  const goCart = useCallback(() => navigate('/cart'), [navigate])
  const signIn = { to: authed ? '/' : '/signin', label: authed ? 'Open console' : 'Sign in', router: true }
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const matches = detect(`${form.message} ${form.company}`)
  // Design-as-data: animation + the live-intent panel are headless toggles (the
  // `contact` override). Off by default → a clean, static content page.
  const anim = c.animation === true
  const showIntent = anim && c.showIntent !== false

  async function submit(e) {
    e.preventDefault()
    if (status === 'sending') return
    setStatus('sending'); setMsg('')
    try {
      const { data } = await axios.post('/api/v1/site/contact', form)
      if (data && data.ok) { setStatus('sent'); setMsg(data.message || '') }
      else { setStatus('error'); setMsg((data && data.error) || 'Something went wrong.') }
    } catch (err) { setStatus('error'); setMsg(err?.response?.data?.error || 'Could not send — please try again.') }
  }

  const h1i = c.h1Accent ? c.h1.toLowerCase().indexOf(String(c.h1Accent).toLowerCase()) : -1

  return (
    <div className={`mkt ${anim ? 'rrc-anim' : ''} min-h-screen bg-gray-50 text-gray-900 antialiased`}>
      <MarketingNav services={services} cartCount={seatCount} onOpenCart={goCart} signIn={signIn} />

      <section className="relative overflow-hidden">
        {/* soft drifting aurora — only when animation is on */}
        {anim && (
          <div aria-hidden className="c-aurora pointer-events-none absolute -top-56 left-1/2 -translate-x-1/2 w-[900px] h-[820px] rounded-full"
            style={{ background: 'radial-gradient(closest-side, rgba(229,55,43,0.16), rgba(229,55,43,0) 70%)' }} />
        )}

        <div className="relative max-w-6xl mx-auto px-5 pt-20 sm:pt-24 pb-24 grid lg:grid-cols-[1.05fr_.95fr] gap-12 lg:gap-16 items-start">
          {/* ── left: pitch + live intent + ways to reach ── */}
          <div>
            <p className="c-rise text-[11px] font-bold uppercase tracking-[0.22em] text-primary-600" style={{ animationDelay: '40ms' }}>{c.eyebrow}</p>
            <h1 className="c-rise font-display font-black tracking-tight leading-[1.02] mt-5 text-[2.9rem] sm:text-6xl text-gray-900" style={{ animationDelay: '110ms', textWrap: 'balance' }}>
              {h1i < 0 ? c.h1 : <>{c.h1.slice(0, h1i)}<span className="serif text-primary-600">{c.h1.slice(h1i, h1i + c.h1Accent.length)}</span>{c.h1.slice(h1i + c.h1Accent.length)}</>}
            </h1>
            <p className="c-rise mt-6 text-gray-600 text-[1.06rem] leading-relaxed max-w-md" style={{ animationDelay: '200ms' }}>{c.subhead}</p>

            {/* LIVE INTENT — headless-toggled: the page reads the message as you type */}
            {showIntent && (
              <div className="c-rise mt-9 max-w-md" style={{ animationDelay: '280ms' }}>
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400">
                  <span className={`w-1.5 h-1.5 rounded-full bg-primary-600 ${matches.length ? 'c-scan' : ''}`} />
                  {matches.length ? 'AIRA is mapping your request →' : "Start typing below — we'll map it to services live"}
                </div>
                <div className="mt-3 flex flex-wrap gap-2 min-h-[38px]">
                  {matches.map((m, i) => (
                    <span key={m} className="c-chip inline-flex items-center gap-1.5 rounded-full border border-primary-500/30 bg-primary-500/10 text-primary-600 text-[13px] font-medium px-3 py-1.5" style={{ animationDelay: `${i * 55}ms` }}>
                      <Sparkles size={12} /> {m}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="c-rise mt-8 space-y-3 max-w-md" style={{ animationDelay: '340ms' }}>
              <a href={`mailto:${c.email}`} className="glass group flex items-center gap-3.5 rounded-2xl px-4 py-3.5 border border-gray-200 hover:border-gray-300 transition-colors">
                <span className="w-9 h-9 rounded-xl bg-primary-500/10 flex items-center justify-center shrink-0"><Mail size={16} className="text-primary-600" /></span>
                <span className="min-w-0"><span className="block text-[13px] text-gray-400">{c.emailNote}</span><span className="block text-[15px] font-semibold text-gray-900">{c.email}</span></span>
                <ArrowUpRight size={16} className="ml-auto text-gray-400 group-hover:text-primary-600 transition-colors" />
              </a>
              <div className="glass flex items-center gap-3.5 rounded-2xl px-4 py-3.5 border border-gray-200">
                <span className="w-9 h-9 rounded-xl bg-primary-500/10 flex items-center justify-center shrink-0"><MessageSquare size={16} className="text-primary-600" /></span>
                <span className="min-w-0"><span className="block text-[13px] text-gray-400">Prefer to talk now?</span><span className="block text-[15px] font-semibold text-gray-900">{c.chatNote}</span></span>
              </div>
            </div>
          </div>

          {/* ── right: the form card ── */}
          <div className="c-rise glass relative rounded-3xl border border-gray-200 p-6 sm:p-8" style={{ animationDelay: '240ms' }}>
            {status === 'sent' ? (
              <div className="min-h-[440px] flex flex-col items-center justify-center text-center px-2">
                <div className="relative w-24 h-24 mb-2">
                  <span className="absolute inset-0 rounded-full border border-primary-500/50 c-bloom" />
                  <span className="absolute inset-0 rounded-full border border-primary-500/40 c-bloom-2" />
                  <svg viewBox="0 0 64 64" className="c-check relative w-24 h-24" fill="none">
                    <circle cx="32" cy="32" r="27" stroke="var(--accent)" strokeWidth="2.5" />
                    <path d="M21 33l7.5 7.5L44 24" stroke="var(--accent-ink)" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h2 className="c-pop font-display text-2xl font-bold text-gray-900 mt-3">{c.successTitle}</h2>
                <p className="c-pop text-gray-600 mt-3 max-w-xs leading-relaxed">{msg || `Thanks — we've got your message and will reply to ${form.email} soon.`}</p>
                <a href="/products" className="c-pop mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:underline">Explore the 15 services <ArrowUpRight size={15} /></a>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <h2 className="c-rise font-display text-xl font-bold text-gray-900" style={{ animationDelay: '280ms' }}>{c.formTitle}</h2>
                  <p className="c-rise text-[13px] text-gray-400 mt-1" style={{ animationDelay: '320ms' }}>{c.formSubhead}</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your name" value={form.name} onChange={set('name')} placeholder="Jordan Rivera" delay={340} autoComplete="name" />
                  <Field label="Work email" type="email" required value={form.email} onChange={set('email')} placeholder="you@company.com" delay={380} autoComplete="email" />
                </div>
                <Field label="Company" value={form.company} onChange={set('company')} placeholder="Acme Inc. (optional)" delay={420} autoComplete="organization" />
                <div className="c-rise" style={{ animationDelay: '460ms' }}>
                  <label className="c-label">How can we help?<span className="text-primary-600"> *</span></label>
                  <div className="c-field"><textarea className="c-input resize-none" rows={4} value={form.message} onChange={set('message')} required placeholder="I want a chatbot on our docs that books demos…" /></div>
                </div>
                {status === 'error' && <p className="text-sm text-primary-600">{msg}</p>}
                <button type="submit" disabled={status === 'sending'}
                  className={`c-send c-rise w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary-600 text-white text-[15px] font-semibold px-5 py-3.5 hover:bg-primary-700 transition-colors disabled:opacity-70 ${status === 'sending' ? 'c-sending' : ''}`}
                  style={{ animationDelay: '500ms' }}>
                  {status === 'sending' ? 'Sending…' : 'Send message'} <Send size={16} />
                </button>
                <p className="c-rise text-center text-[12px] text-gray-400" style={{ animationDelay: '540ms' }}>{c.privacyNote}</p>
              </form>
            )}
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
